/**
 * User Games Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  DataSourceConfig 
} from '../types';
import { Game } from "@/types-games";
import { formatDate } from '../shared/formatters';

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  name: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
    required: true,
  },
  description: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: false,
  },
  type: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  created_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  last_updated: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  }
};

// Column definitions
const columns: ColumnDefinition[] = [
  { key: 'name', label: 'Game Name', filterable: true, metadata: fields.name },
  { key: 'description', label: 'Description', filterable: true, metadata: fields.description },
  { key: 'type', label: 'Type', filterable: true, metadata: fields.type },
  { key: 'created_at', label: 'Created', align: 'left', metadata: fields.created_at },
  { key: 'last_updated', label: 'Last Updated', align: 'left', metadata: fields.last_updated }
];

// Configuration
const config: DataSourceConfig = {
  type: 'user-games',
  title: 'User Games',
  description: 'Games available to the user',
  expandable: false,
};

// Data fetcher (games are fetched from provider, pass them in)
function fetchData(selectedGame?: any, selectedFolderId?: string | null, games?: Game[]): Game[] {
  return games || [];
}

// Transformers
function toList(rawData: Game[]): ListWidgetItem[] {
  return rawData.map(game => ({
    name: game.name,
    tags: 'Game',
    type: 'Game',
    created_at: game.created_at,
    data: game.settings || {},
    updated_at: game.last_updated,
    description: game.description || 'No description'
  }));
}

function toTable(rawData: Game[]): TableDataOutput {
  return {
    columns,
    data: rawData.map(game => ({
      id: game.id,
      name: String(game.name),
      description: String(game.description || 'No description'),
      type: 'Game',
      created_at: formatDate(game.created_at),
      last_updated: formatDate(game.last_updated)
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

// Export the module
export const userGamesDataSource: DataSourceModule = {
  type: 'user-games',
  config,
  fields,
  columns,
  fetchData,
  transformers: {
    toList,
    toTable
  }
};

